import { API_BASE_URL } from '../config/api';

export interface GoogleAuthStatus {
  hasGoogleAuth: boolean;
  isTokenValid: boolean;
  tokenExpiry: string | null;
}

export interface GoogleSheetsExportResult {
  spreadsheetId: string;
  spreadsheetUrl: string;
  title: string;
  rowCount: number;
}

/**
 * Get Google OAuth URL to start authentication flow
 */
export async function getGoogleAuthUrl(): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/google-auth/url`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get auth URL' }));
    throw new Error(error.error || 'Failed to get auth URL');
  }

  const data = await response.json();
  return data.url;
}

/**
 * Complete Google OAuth flow with authorization code
 */
export async function completeGoogleAuth(code: string): Promise<{ token: string; user: any }> {
  const response = await fetch(`${API_BASE_URL}/google-auth/callback`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Authentication failed' }));
    throw new Error(error.error || 'Authentication failed');
  }

  const data = await response.json();
  return data;
}

/**
 * Check Google authentication status
 */
export async function getGoogleAuthStatus(): Promise<GoogleAuthStatus> {
  const response = await fetch(`${API_BASE_URL}/google-auth/status`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to check auth status' }));
    throw new Error(error.error || 'Failed to check auth status');
  }

  const data = await response.json();
  return data;
}

/**
 * Disconnect Google account
 */
export async function disconnectGoogleAccount(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/google-auth/disconnect`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to disconnect' }));
    throw new Error(error.error || 'Failed to disconnect');
  }
}

/**
 * Export data to new Google Sheet
 */
export async function exportToGoogleSheets(
  data: any[],
  title?: string,
  columns?: string[]
): Promise<GoogleSheetsExportResult> {
  const response = await fetch(`${API_BASE_URL}/google-sheets/export`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data, title, columns }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to export' }));

    if (error.requireAuth) {
      throw new Error('GOOGLE_AUTH_REQUIRED');
    }

    throw new Error(error.error || 'Failed to export to Google Sheets');
  }

  const result = await response.json();
  return result;
}

/**
 * Append data to existing Google Sheet
 */
export async function appendToGoogleSheet(
  spreadsheetId: string,
  data: any[],
  sheetName = 'Data',
  columns?: string[]
): Promise<{ spreadsheetId: string; rowsAdded: number; updatedRange: string }> {
  const response = await fetch(`${API_BASE_URL}/google-sheets/append`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ spreadsheetId, data, sheetName, columns }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to append' }));

    if (error.requireAuth) {
      throw new Error('GOOGLE_AUTH_REQUIRED');
    }

    throw new Error(error.error || 'Failed to append to Google Sheets');
  }

  const result = await response.json();
  return result;
}
